import React, { useState } from "react";
import { FiUploadCloud } from "react-icons/fi";
import "../SheetUpload/SheetBulkUploadPopup.scss";
import { toast } from "react-toastify";
import { exitApi } from "./exitApi";

const MAX_SIZE = 250 * 1024 * 1024; // 250MB

const cell = { padding: "6px 8px", borderBottom: "1px solid #eceef1", textAlign: "left", fontSize: 13 };

// CR-4 flow: download template -> upload -> preview (rows passed / failed with reason) -> confirm.
// Confirm posts the same file again; the backend re-checks every row while creating the exits.
const ExitBulkInitiatePopup = ({ setPopup, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const validateFile = (uploadedFile) => {
    if (!uploadedFile) return false;
    const name = uploadedFile.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".csv")) {
      toast.error("Only .xlsx or .csv files are allowed");
      return false;
    }
    if (uploadedFile.size > MAX_SIZE) {
      toast.error("File size should be less than 250MB");
      return false;
    }
    return true;
  };

  const pick = (uploadedFile) => {
    if (!validateFile(uploadedFile)) return;
    setFile(uploadedFile);
    setPreview(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    pick(e.dataTransfer.files[0]);
  };

  const runPreview = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    setBusy(true);
    try {
      setPreview((await exitApi.bulkInitiatePreview(file)).data);
    } catch {
      /* toast shown by exitApi */
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await exitApi.bulkInitiate(file);
      toast.success(res.message || "Upload Started");
      setPopup(false);
      onUploaded && onUploaded();
    } catch {
      /* toast shown by exitApi */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay">
      <div className="popupContainers" style={preview ? { width: 900, maxWidth: "95vw" } : undefined}>
        <div className="popupHeader">
          <div />
          <span className="TitleHeader">Bulk Exit Upload</span>
          <button className="closeButton" onClick={() => setPopup(false)}>
            ×
          </button>
        </div>

        {!preview ? (
          <>
            <div className="uploadTypeHeader" style={{ justifyContent: "flex-end", gap: 10 }}>
              <button className="sampleSheetBtn" onClick={() => exitApi.bulkInitiateTemplate("xlsx")}>
                Template (.xlsx)
              </button>
              <button className="sampleSheetBtn" onClick={() => exitApi.bulkInitiateTemplate("csv")}>
                Template (.csv)
              </button>
            </div>

            <div className="uploadBox" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
              <FiUploadCloud className="uploadIcon" />
              <p className="mainText">Click to select or drag & drop your file</p>
              <p className="subText">Supported formats: .xlsx, .csv · dates as DD-MM-YYYY</p>
              <p className="subText">Max file size 250MB</p>
              <input type="file" onChange={(e) => pick(e.target.files[0])} accept=".xlsx,.csv" />
              {file && <p className="fileName">{file.name}</p>}
            </div>

            <button className="submitBtn" disabled={!file || busy} onClick={runPreview}>
              {busy ? "Checking..." : "Preview"}
            </button>
          </>
        ) : (
          <>
            <p style={{ margin: "12px 0" }}>
              <b>{preview.total_count}</b> rows · <span style={{ color: "#16803c" }}>{preview.passed_count} passed</span> ·{" "}
              <span style={{ color: "#c62828" }}>{preview.failed_count} failed</span>
              {preview.failed_count > 0 && " — failed rows are skipped and come back in the error file to correct and re-upload."}
            </p>
            <div style={{ maxHeight: 360, overflow: "auto", border: "1px solid #eceef1", borderRadius: 4 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: "#f7f8fa" }}>
                  <tr>
                    <th style={cell}>Row</th>
                    <th style={cell}>Employee Code</th>
                    <th style={cell}>Employee Name</th>
                    <th style={cell}>Result</th>
                    <th style={cell}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row_no}>
                      <td style={cell}>{r.row_no}</td>
                      <td style={cell}>{r.emp_code}</td>
                      <td style={cell}>{r.emp_name || "—"}</td>
                      <td style={{ ...cell, color: r.status === "passed" ? "#16803c" : "#c62828" }}>
                        {r.status === "passed" ? "Passed" : "Failed"}
                      </td>
                      <td style={cell}>{r.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="submitBtn" style={{ background: "#9aa0a6" }} disabled={busy} onClick={() => setPreview(null)}>
                Back
              </button>
              <button className="submitBtn" disabled={busy || preview.passed_count === 0} onClick={confirm}>
                {busy ? "Starting..." : `Confirm ${preview.passed_count} exit${preview.passed_count === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExitBulkInitiatePopup;
